import { loan_application, Product } from '@axo/shared/data-access/types';
import { ApplicationDataDateField } from '../../Fields/ApplicationDataDateField';
import { ApplicationDataSelectField } from '../../Fields/ApplicationDataSelectField';
import { ApplicantDataApplicantTabPanelSection } from '../ApplicationDataApplicantTabPanelLayout';
import { ApplicationDataCurrencyField } from '../../Fields/ApplicationDataCurrencyField';
import { ApplicationDataChildrenField } from '../../Fields/ApplicationDataChildrenField';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { MarketCountryCode } from '@axo/shared/types';
import { Broker } from '@axo/shared/data-access/types/broker';
import { useMaritalStatusOptions } from './options/useMaritalStatusOptions';
import { ApplicationConditionalWrapper } from '../../../ConditionalWrappers/ApplicationConditionalWrapper';
import { useHousingTypeOptions } from './options/useHousingTypeOptions';

interface HouseholdSectionProps {
  applicationID: string;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const HouseholdSection: React.FC<HouseholdSectionProps> = ({
  applicationID,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const { setValue } = useFormContext<ApplicationDataFormValues>();

  const maritalStatusOptions = useMaritalStatusOptions();
  const housingTypeOptions = useHousingTypeOptions();

  const isCoApplicant = coApplicantCustomerID || coApplicantPersonID;

  return (
    <ApplicantDataApplicantTabPanelSection title="Household">
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Sweden,
                MarketCountryCode.Norway,
              ],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantIsCohabitant"
            label="Cohabitanting with main applicant"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationDataSelectField
          applicationID={applicationID}
          name="MaritalStatus"
          label="Relationship status"
          options={maritalStatusOptions}
        />
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Sweden,
                MarketCountryCode.Norway,
              ],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantMaritalStatus"
            label="Relationship status"
            options={maritalStatusOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="PartnerNetIncomeYearly"
            label="Spouse yearly income after tax"
            onChangeSideEffect={(value) =>
              setValue('PartnerNetIncomeYearly', value)
            }
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.CreditCard, Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Sweden,
                MarketCountryCode.Finland,
              ],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="PartnerGrossIncomeMonthly"
            label="Spouse monthly income before tax"
            onChangeSideEffect={(value) =>
              setValue('PartnerGrossIncomeYearly', value * 12)
            }
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.CreditCard, Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Sweden,
                MarketCountryCode.Finland,
              ],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="PartnerGrossIncomeYearly"
            label="Spouse yearly income before tax"
            onChangeSideEffect={(value) =>
              setValue('PartnerGrossIncomeMonthly', Math.round(value / 12))
            }
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.CreditCard, Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Finland,
                MarketCountryCode.Sweden,
              ],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="CoApplicantPartnerGrossIncomeYearly"
            label="Spouse yearly income before tax"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              productType: [Product.UnsecuredLoan],
              broker: [Broker.Zmarta],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="PartnerLivingCostMonthly"
            label="Spouse living cost monthly"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationDataChildrenField
          applicationID={applicationID}
          label="Children"
        />
      )}
      {!isCoApplicant && (
        <ApplicationDataSelectField
          applicationID={applicationID}
          name="HousingType"
          label="Residential status"
          options={housingTypeOptions}
        />
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Sweden],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantHousingType"
            label="Residential status"
            options={housingTypeOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Finland],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="ApartmentType"
            label="Apartment type"
            options={[
              { value: null, label: '-' },
              ...Object.values(loan_application.ApartmentType).map((value) => ({
                value,
              })),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Denmark,
              ],
            },
            {
              productType: [Product.CreditCard],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="LivedAtAddressSince"
            label="Lived at this address"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Finland,
                MarketCountryCode.Sweden,
                MarketCountryCode.Denmark,
              ],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="RentCostMonthly"
            label="Share of monthly rent"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Sweden],
            },
            {
              productType: [Product.SecuredLoan],
              marketCountry: [MarketCountryCode.Norway],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="CoApplicantRentCostMonthly"
            label="Share of monthly rent"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Finland],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="OwnerAssociationCostMonthly"
            label="Owner's association monthly cost"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="HasCars"
            label="Has car"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CarOwnerShipType"
            label="Car ownership"
            options={[
              { value: null, label: '-' },
              ...Object.values(loan_application.CarOwnerShipType).map(
                (value) => ({
                  value,
                }),
              ),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="CarTotalCostMonthly"
            label="Car monthly payment"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="OwnsHolidayHome"
            label="Holiday home"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
    </ApplicantDataApplicantTabPanelSection>
  );
};
